import { ApolloQueryResult } from '@apollo/client';
import * as queryTypes from './graphqlTypes';
import setters from '../actions';
import getters from '../getters';
import { setFilterColumn } from '../utils';
import * as lodash from 'lodash';
import { updateUserSiganature } from '../banner/actionCreators';

export const findProduct =
  (findProductInput: FindProductInput) => (dispatch: DispatchType) =>
    new Promise<Product>((resolve, reject) => {
      const {
        graphql: { QUERY },
      } = setters(dispatch);
      QUERY<FindProductQuery>({
        query: queryTypes.FIND_PRODUCT,
        variables: {
          findProductInput,
        },
      })
        .then((result) => {
          const data = result.data.findProduct;
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });

export const findFilteredProducts =
  (searchFilter: SearchFilter) =>
  (dispatch: DispatchType, getState: GetStateType) =>
    new Promise<PaginatedResponse<FilteredProduct[]>>((resolve, reject) => {
      const {
        user: { getSelectedFilial },
      } = getters(getState());
      const {
        graphql: { QUERY },
      } = setters(dispatch);

      const filial = getSelectedFilial();
      QUERY<FindFilteredProductsQuery>({
        query: queryTypes.FIND_FILTERED_PRODUCTS,
        variables: {
          filialIds: [filial.id],
          searchFilter,
        },
      })
        .then((result) => {
          resolve(result.data.findFilteredProducts);
        })
        .catch((error) => {
          reject(error);
        });
    });

export const filterEsProductsQuery =
  (searchFilter: SearchFilter) => (dispatch: DispatchType) =>
    new Promise<ApolloQueryResult<FilterEsProductsQuery>>((resolve, reject) => {
      const {
        graphql: { QUERY },
      } = setters(dispatch);

      QUERY<FilterEsProductsQuery>({
        query: queryTypes.FILTER_ES_PRODUCTS,
        variables: {
          searchFilter,
        },
      })
        .then(resolve)
        .catch(reject);
    });

export const findFilteredPromotionalProducts =
  (searchFilter: SearchFilter) =>
  (dispatch: DispatchType, getState: GetStateType) =>
    new Promise<PaginatedResponse<FilteredPromotionalProduct[]>>(
      (resolve, reject) => {
        const {
          user: { getSelectedFilial },
        } = getters(getState());
        const {
          graphql: { QUERY },
        } = setters(dispatch);

        const filial = getSelectedFilial();
        QUERY<FindFilteredPromotionalProductsQuery>({
          query: queryTypes.FIND_FILTERED_PROMOTIONAL_PRODUCTS,
          variables: {
            filialIds: [filial.id],
            searchFilter,
          },
        })
          .then((result) => {
            resolve(result.data.findFilteredPromotionalProducts);
          })
          .catch((error) => {
            reject(error);
          });
      },
    );

export const findFilteredOfflineProducts =
  (searchFilter: SearchFilter) =>
  (dispatch: DispatchType, getState: GetStateType) =>
    new Promise<PaginatedResponse<FilteredOfflineProduct[]>>(
      (resolve, reject) => {
        const {
          user: { getSelectedFilial },
        } = getters(getState());
        const {
          graphql: { QUERY },
        } = setters(dispatch);

        const filial = getSelectedFilial();
        QUERY<FindFilteredOfflineProductsQuery>({
          query: queryTypes.FIND_FILTERED_OFFLINE_PRODUCTS,
          variables: {
            filialIds: [filial.id],
            searchFilter,
          },
        })
          .then((result) => {
            resolve(result.data.findFilteredOfflineProducts);
          })
          .catch((error) => {
            reject(error);
          });
      },
    );

export const autocompletePromotionalProducts =
  (autocompleteFilter: AutocompleteFilter) =>
  (dispatch: DispatchType, getState: GetStateType) =>
    new Promise<string[]>((resolve, reject) => {
      const {
        user: { getSelectedFilial },
      } = getters(getState());
      const {
        graphql: { QUERY },
      } = setters(dispatch);
      const filial = getSelectedFilial();
      QUERY<AutocompletePromotionalProductsQuery>({
        query: queryTypes.AUTOCOMPLETE_PROMOTIONAL_PRODUCTS,
        variables: {
          filialIds: [filial.id],
          autocompleteFilter: autocompleteFilter,
        },
      })
        .then((result) => {
          resolve(result.data.autocompletePromotionalProducts);
        })
        .catch((error) => {
          reject(error);
        });
    });

export const autocompletePromotionalProductItems =
  (field: keyof ProductState['autocompletes']['promotion']) =>
  (dispatch: DispatchType, getState: GetStateType) =>
    new Promise<string[]>((resolve, reject) => {
      const {
        product: { getProduct },
      } = getters(getState());
      const {
        product: { autocompletePromotionalProducts },
      } = setters(dispatch);
      const filter = getProduct(`autocompletes.promotion.${field}.filter`);
      autocompletePromotionalProducts(filter).then(resolve).catch(reject);
    });

export const autocompleteEsProductsQuery =
  (autocompleteFilter: AutocompleteFilter) => (dispatch: DispatchType) =>
    new Promise<AutocompleteResponse>((resolve, reject) => {
      const {
        graphql: { QUERY },
      } = setters(dispatch);
      QUERY<AutocompleteEsProductsQuery>({
        query: queryTypes.AUTOCOMPLETE_ES_PRODUCTS,
        variables: {
          autocompleteFilter: autocompleteFilter,
        },
      })
        .then((result) => {
          resolve(result.data.autocompleteEsProducts);
        })
        .catch(reject);
    });

export const autocompleteOfflineProducts =
  (autocompleteFilter: AutocompleteFilter) =>
  (dispatch: DispatchType, getState: GetStateType) =>
    new Promise<string[]>((resolve, reject) => {
      const {
        user: { getSelectedFilial },
      } = getters(getState());
      const {
        graphql: { QUERY },
      } = setters(dispatch);
      const filial = getSelectedFilial();
      QUERY<AutocompleteOfflineProductsQuery>({
        query: queryTypes.AUTOCOMPLETE_OFFLINE_PRODUCTS,
        variables: {
          filialIds: [filial.id],
          autocompleteFilter: autocompleteFilter,
        },
      })
        .then((result) => {
          resolve(result.data.autocompleteOfflineProducts);
        })
        .catch((error) => {
          reject(error);
        });
    });

export const findProductsByFilter =
  (filter: keyof DeepType<ProductState, 'filter'>, page = 1, limit = 15) =>
  (dispatch: DispatchType, getState: GetStateType) =>
    new Promise<ApolloQueryResult<FindProductsByFilterQuery>>(
      (resolve, reject) => {
        const {
          product: { getProduct },
          user: { getSelectedFilial },
        } = getters(getState());
        const {
          graphql: { QUERY },
          product: { setProduct },
        } = setters(dispatch);
        const filial = getSelectedFilial();
        QUERY<FindProductsByFilterQuery>({
          query: queryTypes.FIND_PRODUCTS_BY_FILTER,
          variables: {
            filialId: filial.id,
            filter: getProduct(`filter.${filter}`),
            page,
            limit,
          },
        })
          .then((result) => {
            setProduct({
              field: `filtered.${filter}`,
              value: result.data.FindProductsByFilter,
            });
            resolve(result);
          })
          .catch((error) => {
            reject(error);
          });
      },
    );

export const listProductsByFilter =
  (filter: string, page = 1, limit = 15) =>
  (dispatch: DispatchType, getState: GetStateType) =>
    new Promise<ApolloQueryResult<FindProductsByFilterQuery>>(
      (resolve, reject) => {
        const {
          user: { getSelectedFilial },
        } = getters(getState());
        const {
          graphql: { QUERY },
        } = setters(dispatch);
        const filial = getSelectedFilial();
        QUERY<FindProductsByFilterQuery>({
          query: queryTypes.FIND_PRODUCTS_BY_FILTER,
          variables: {
            filialId: filial.id,
            filter,
            page,
            limit,
          },
        })
          .then((result) => {
            resolve(result);
          })
          .catch((error) => {
            reject(error);
          });
      },
    );

export const createSingleFormsForProducts =
  (products: FilteredProductItem[]) =>
  (dispatch: DispatchType, getState: GetStateType) => {
    const {
      form: { getForm },
      banner: { getDefaultBannerForm },
    } = getters(getState());

    const {
      form: { setForm },
    } = setters(dispatch);

    const singleForms = getForm('batchBanner.singleForms');
    const showSingleFormId = getForm('batchBanner.showSingleFormId');
    const maxId = singleForms.reduce((acc, { id }) => (id > acc ? id : acc), 0);
    let newSingleForms = [
      ...singleForms,
      ...products.map((product, index) => {
        const { type, code, ean, description, price, promotion } = product;
        const id = maxId + index + 1;
        const { regularPrice } = price;
        const promotionalPrice =
          (promotion ? promotion.promotionalPrice : price.promotionalPrice) ||
          regularPrice;
        const formatDate = (date) =>
          new Date(date).toLocaleString('pt-BR').split(',')[0];
        let newSingleForm: SingleBannerForm = lodash.merge(
          {},
          getDefaultBannerForm(),
          {
            id,
            product: {
              type,
              product: {
                ...product,
                identifier: id,
              },
              primary: description,
              code,
              unit: product.exitPackaging ?? 'UN',
            },
            price: {
              barcode: ean.padStart(13, '0'),
              price: regularPrice,
              promotional: promotionalPrice,
              min: Math.min(regularPrice, promotionalPrice) || regularPrice,
              discount:
                Math.abs(regularPrice) > 1e-7
                  ? ((regularPrice - promotionalPrice) * 100) / regularPrice
                  : 0,
              expirationStart: formatDate(
                (promotion
                  ? promotion.promotionalStartDate
                  : price.promotionalStartDate) ?? new Date(),
              ),
              expirationEnd: formatDate(
                (promotion
                  ? promotion.promotionalEndDate
                  : price.promotionalEndDate) ?? new Date(),
              ),
            },
          } as DeepPartial<SingleBannerForm>,
        );

        if (promotion) {
          const promotionDescription = (
            promotion.description ?? 'GERAL'
          ).trim();
          const promotionType = promotion?.type
            ? `${promotion.type.trim()}`
            : '';
          newSingleForm.config.autocompletes.promotion.value =
            promotionDescription;
          newSingleForm.config.autocompletes.type.value = promotionType;
          newSingleForm.config.filter = setFilterColumn(
            'promotion',
            promotionDescription,
            newSingleForm.config.filter,
            'LIKE',
            'AND',
          );
          newSingleForm.config.filter = setFilterColumn(
            'type',
            promotionType,
            newSingleForm.config.filter,
            'LIKE',
            'AND',
          );
          newSingleForm.config.filter = setFilterColumn(
            'resume',
            `${code} - ${description?.trim()} - ${ean.padStart(
              13,
              '0',
            )} - ${promotionType} - ${promotionDescription}`,
            newSingleForm.config.filter,
            'LIKE',
            'AND',
          );
        } else if (price.promotionalPrice > 0 && type === 'promotion') {
          newSingleForm.config.autocompletes.promotion.value = 'GERAL';
          newSingleForm.config.autocompletes.type.value = '';
          newSingleForm.config.filter = setFilterColumn(
            'promotion',
            'GERAL',
            newSingleForm.config.filter,
            'LIKE',
            'AND',
          );
          newSingleForm.config.filter = setFilterColumn(
            'type',
            '',
            newSingleForm.config.filter,
            'LIKE',
            'AND',
          );
          newSingleForm.config.filter = setFilterColumn(
            'resume',
            `${code} - ${description?.trim()} - ${ean.padStart(
              13,
              '0',
            )} - GERAL`,
            newSingleForm.config.filter,
            'LIKE',
            'AND',
          );
        } else {
          newSingleForm.config.autocompletes.promotion.value = '';
          newSingleForm.config.autocompletes.type.value = '';
          newSingleForm.config.filter = setFilterColumn(
            'promotion',
            '',
            newSingleForm.config.filter,
            'LIKE',
            'AND',
          );
          newSingleForm.config.filter = setFilterColumn(
            'type',
            '',
            newSingleForm.config.filter,
            'LIKE',
            'AND',
          );
          newSingleForm.config.filter = setFilterColumn(
            'resume',
            `${code} - ${description?.trim()} - ${ean.padStart(13, '0')}`,
            newSingleForm.config.filter,
            'LIKE',
            'AND',
          );
        }
        return newSingleForm;
      }),
    ];

    setForm('batchBanner.singleForms', newSingleForms);
    if (showSingleFormId === 0) {
      setForm('batchBanner.showSingleFormId', newSingleForms[0].id);
    }
    dispatch(updateUserSiganature());
  };

export const updateSingleFormForProduct =
  ({ identifier, ...product }: FilteredProductItem) =>
  (dispatch: DispatchType) => {
    const {
      form: { setBatchForm },
    } = setters(dispatch);

    const { type, description, ean, code, price, promotion } = product;
    const { regularPrice } = price;
    const promotionalPrice =
      (promotion ? promotion.promotionalPrice : price.promotionalPrice) ||
      regularPrice;

    const formatDate = (date) =>
      new Date(date).toLocaleString('pt-BR').split(',')[0];

    let newSingleForm: DeepPartial<SingleBannerForm> = {
      product: {
        type,
        product: {
          ...product,
        },
        primary: description,
        code,
        unit: product.exitPackaging ?? 'UN',
      },
      price: {
        barcode: ean.padStart(13, '0'),
        price: regularPrice,
        promotional: promotionalPrice ?? 0,
        min: Math.min(regularPrice, promotionalPrice) || regularPrice,
        discount:
          Math.abs(regularPrice) > 1e-7
            ? ((regularPrice - promotionalPrice) * 100) / regularPrice
            : 0,
        expirationStart: formatDate(
          (promotion
            ? promotion.promotionalStartDate
            : price.promotionalStartDate) ?? new Date(),
        ),
        expirationEnd: formatDate(
          (promotion
            ? promotion.promotionalEndDate
            : price.promotionalEndDate) ?? new Date(),
        ),
      },
    };

    setBatchForm(null, newSingleForm);
    dispatch(updateUserSiganature());
  };

const productActionCreators = (dispatch: <T>(action: any) => T) =>
  Object.assign(
    {},
    {
      findProductsByFilter: (
        filter: keyof DeepType<ProductState, 'filter'>,
        page = 1,
        limit = 15,
      ) =>
        dispatch<Promise<ApolloQueryResult<FindProductsByFilterQuery>>>(
          findProductsByFilter(filter, page, limit),
        ),
      findFilteredProducts: (searchFilter: SearchFilter) =>
        dispatch<Promise<PaginatedResponse<FilteredProduct[]>>>(
          findFilteredProducts(searchFilter),
        ),
      findFilteredPromotionalProducts: (searchFilter: SearchFilter) =>
        dispatch<Promise<PaginatedResponse<FilteredPromotionalProduct[]>>>(
          findFilteredPromotionalProducts(searchFilter),
        ),
      filterEsProductsQuery: (searchFilter: SearchFilter) =>
        dispatch<Promise<ApolloQueryResult<FilterEsProductsQuery>>>(
          filterEsProductsQuery(searchFilter),
        ),
      findFilteredOfflineProducts: (searchFilter: SearchFilter) =>
        dispatch<Promise<PaginatedResponse<FilteredOfflineProduct[]>>>(
          findFilteredOfflineProducts(searchFilter),
        ),
      listProductsByFilter: (filter: string, page = 1, limit = 15) =>
        dispatch<Promise<ApolloQueryResult<FindProductsByFilterQuery>>>(
          listProductsByFilter(filter, page, limit),
        ),
      autocompletePromotionalProducts: (
        autocompleteFilter: AutocompleteFilter,
      ) =>
        dispatch<Promise<string[]>>(
          autocompletePromotionalProducts(autocompleteFilter),
        ),
      autocompletePromotionalProductItems: (
        field: keyof ProductState['autocompletes']['promotion'],
      ) =>
        dispatch<Promise<string[]>>(autocompletePromotionalProductItems(field)),
      autocompleteOfflineProducts: (autocompleteFilter: AutocompleteFilter) =>
        dispatch<Promise<string[]>>(
          autocompleteOfflineProducts(autocompleteFilter),
        ),
      findProduct: (findProductInput: FindProductInput) =>
        dispatch<Promise<Product>>(findProduct(findProductInput)),
      createSingleFormsForProducts: (products: FilteredProductItem[]) =>
        dispatch<Promise<Product>>(createSingleFormsForProducts(products)),
      updateSingleFormForProduct: (product: FilteredProductItem) =>
        dispatch<Promise<Product>>(updateSingleFormForProduct(product)),
      autocompleteEsProductsQuery: (autocompleteFilter: AutocompleteFilter) =>
        dispatch<Promise<AutocompleteResponse>>(
          autocompleteEsProductsQuery(autocompleteFilter),
        ),
    },
  );
export default productActionCreators;
